import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';

import { showToast } from 'legacy/common/utils/toast';

import useEventCreation from 'legacy/events/hooks/useEventCreation';

import Header from 'legacy/common/components/wysiwyg-form/Header';
import ActionBar from 'legacy/events/components/create-event/ActionBar';
import BasicForm from 'legacy/events/components/create-event/BasicForm';
import EventSettings from 'legacy/events/components/create-event/EventSettings';
import PricingTiersModal from 'legacy/events/components/create-event/settings/PricingTiersModal';
import TrackingLinksPage from 'legacy/events/components/create-event/TrackingLinksPage';
import DiscountCodesPage from 'legacy/events/components/create-event/DiscountCodePage';
import PublishModal from 'legacy/events/components/create-event/PublishModal';
import { isBounceWebview, postMessageToRN } from 'legacy/common/utils/webview';
import { WEBVIEW_CLOSE } from 'legacy/common/constants/webview';
import { Event } from 'legacy/common/types/models';
import EventSkeleton from 'legacy/events/components/event-page/EventSkeleton';
import PublicationRequestedModal from '../components/create-event/PublicationRequestedModal';
import RequestToPublishForm from '../components/create-event/RequestToPublishForm';
import RecurringSettings from '../components/create-event/RecurringSettings';
import OffersPage from '../components/create-event/Offers';
import { OffersStep } from '../offers.types';

const EventSchema = Yup.object({
  photo: Yup.object({
    url: Yup.string().required('Required field')
  }),
  title: Yup.string().required('Required field'),
  address: Yup.string().required('Required field'),
  startDate: Yup.string().required('Required field'),
  description: Yup.string().required('Required field')
});

enum Screens {
  BASIC_FORM = 'basicForm',
  REQUEST_TO_PUBLISH_FORM = 'requestToPublishForm',
  EVENT_SETTINGS = 'eventSettings',
  TRACKING_LINKS = 'trackingLinks',
  DISCOUNT_CODES = 'discountCodes',
  RECURRING_SETTINGS = 'recurringSettings',
  OFFERS = 'offers'
}

const getOffersStepTitle = (offersStep: OffersStep) => {
  switch (offersStep) {
    case OffersStep.CREATE_OFFER:
      return 'New Offer';
    case OffersStep.OFFERS_LIST:
      return 'Offers';
    case OffersStep.EDIT_OFFER:
      return 'Edit Offer';
    case OffersStep.OFFER_DETAILS:
      return 'Offer Details';
    default:
      return 'Offers';
  }
};

const screenMap: Record<
  string,
  {
    title: string;
    renderComponent: (options: {
      eventId: string;
      isLoading: boolean;
      shouldRequestToPublish: boolean;
      requestToPublishFormId?: string;
      eventSeriesId?: string;
      onClickEventSettings: () => void;
      navigateToRecurringSettings: () => void;
      togglePricingTiersModal: () => void;
      navigateToTrackingLinks: () => void;
      navigateToDiscountCodes: () => void;
      navigateToSeriesSettings: () => void;
      navigateToBasicForm: () => void;
      navigateToOffers: () => void;
      requestPublication: () => void;
      createDraft: (values: any) => Promise<Event>;
      refetchEvent: () => void;
      offersStep: OffersStep;
      setOffersStep: (step: OffersStep) => void;
      handleOfferBackNavigation: () => void;
    }) => React.ReactNode;
    prevScreen?: Screens;
  }
> = {
  [Screens.BASIC_FORM]: {
    title: 'Create Event',
    renderComponent: ({
      eventId,
      shouldRequestToPublish,
      onClickEventSettings,
      navigateToRecurringSettings,
      togglePricingTiersModal,
      createDraft
    }) => (
      <>
        <BasicForm
          eventId={eventId}
          createDraft={createDraft}
          navigateToRecurringSettings={navigateToRecurringSettings}
        />
        <ActionBar
          eventId={eventId}
          shouldRequestToPublish={shouldRequestToPublish}
          onClickEventSettings={onClickEventSettings}
          togglePricingTiersModal={togglePricingTiersModal}
        />
      </>
    )
  },
  [Screens.EVENT_SETTINGS]: {
    title: 'Event Settings',
    renderComponent: ({
      eventId,
      navigateToTrackingLinks,
      navigateToDiscountCodes,
      navigateToSeriesSettings,
      navigateToOffers,
      refetchEvent,
      createDraft,
      setOffersStep
    }) => (
      <EventSettings
        navigateToTrackingLinks={navigateToTrackingLinks}
        navigateToDiscountCodes={navigateToDiscountCodes}
        navigateToSeriesSettings={navigateToSeriesSettings}
        navigateToOffers={navigateToOffers}
        eventId={eventId}
        refetchEvent={refetchEvent}
        createDraft={createDraft}
        setOffersStep={setOffersStep}
      />
    ),
    prevScreen: Screens.BASIC_FORM
  },
  [Screens.RECURRING_SETTINGS]: {
    title: 'Recurring Settings',
    renderComponent: ({
      eventId,
      createDraft,
      navigateToBasicForm,
      eventSeriesId
    }) => (
      <RecurringSettings
        eventId={eventId}
        createDraft={createDraft}
        eventSeriesId={eventSeriesId}
        navigateToBasicForm={navigateToBasicForm}
      />
    ),
    prevScreen: Screens.BASIC_FORM
  },
  [Screens.REQUEST_TO_PUBLISH_FORM]: {
    title: 'Request to publish',
    renderComponent: ({ requestToPublishFormId, requestPublication }) => (
      <RequestToPublishForm
        requestToPublishFormId={requestToPublishFormId}
        requestPublication={requestPublication}
      />
    ),
    prevScreen: Screens.BASIC_FORM
  },
  [Screens.TRACKING_LINKS]: {
    title: 'Tracking Links',
    renderComponent: ({ eventId }) => <TrackingLinksPage eventId={eventId} />,
    prevScreen: Screens.EVENT_SETTINGS
  },
  [Screens.DISCOUNT_CODES]: {
    title: 'Discount codes',
    renderComponent: ({ eventId }) => <DiscountCodesPage eventId={eventId} />,
    prevScreen: Screens.EVENT_SETTINGS
  },
  [Screens.OFFERS]: {
    title: 'Offers',
    renderComponent: ({
      eventId,
      handleOfferBackNavigation,
      offersStep,
      setOffersStep
    }) => (
      <OffersPage
        eventId={eventId}
        onClose={handleOfferBackNavigation}
        step={offersStep}
        setOffersStep={setOffersStep}
      />
    ),
    prevScreen: Screens.EVENT_SETTINGS
  }
};

interface CreateEventProps {
  eventId?: string;
  orgId?: string;
}

const CreateEvent: React.FC<CreateEventProps> = ({ eventId, orgId }) => {
  const router = useRouter();

  const [screen, setScreen] = useState<Screens>(Screens.BASIC_FORM);

  const [showPricingTiers, setShowPricingTiers] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showPublicationRequestedModal, setShowPublicationRequestedModal] =
    useState(false);
  const [offersStep, setOffersStep] = useState<OffersStep>(
    OffersStep.CREATE_OFFER
  );

  const togglePublishModal = () => {
    setShowPublishModal((showPublishModal) => !showPublishModal);
  };

  const eventCreation = useEventCreation({
    eventId,
    orgId
  });

  const onClickEventSettings = useCallback(() => {
    setScreen(Screens.EVENT_SETTINGS);
  }, []);

  const navigateToRecurringSettings = () => {
    setScreen(Screens.RECURRING_SETTINGS);
  };

  const navigateToTrackingLinks = () => {
    setScreen(Screens.TRACKING_LINKS);
  };

  const navigateToDiscountCodes = () => {
    setScreen(Screens.DISCOUNT_CODES);
  };

  const navigateToBasicForm = () => {
    setScreen(Screens.BASIC_FORM);
  };

  const navigateToSeriesSettings = () => {
    setScreen(Screens.RECURRING_SETTINGS);
  };

  const navigateToOffers = () => {
    setScreen(Screens.OFFERS);
  };

  const handleOfferBackNavigation = () => {
    if (offersStep === OffersStep.OFFER_DETAILS) {
      setOffersStep(OffersStep.OFFERS_LIST);
    } else if (offersStep === OffersStep.EDIT_OFFER) {
      setOffersStep(OffersStep.OFFER_DETAILS);
    } else {
      setScreen(Screens.EVENT_SETTINGS);
    }
  };

  const currentScreen = screenMap[screen];

  const requestPublication = async (data?: { [key: string]: unknown }) => {
    await eventCreation.requestPublication(data);
    setShowPublicationRequestedModal(true);
  };

  const onSubmit = async () => {
    if (eventCreation.shouldRequestToPublish) {
      if (eventCreation.requestToPublishFormId) {
        setScreen(Screens.REQUEST_TO_PUBLISH_FORM);
      } else {
        requestPublication();
      }
    } else {
      togglePublishModal();
    }
  };

  const togglePricingTiersModal = () => {
    setShowPricingTiers((showPricingTiers) => !showPricingTiers);
  };

  if (eventCreation.shouldShowEventLoading && eventCreation.isLoadingEvent) {
    return <EventSkeleton />;
  }

  return (
    <Formik
      initialValues={eventCreation.initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      validationSchema={EventSchema}
    >
      {({ values, dirty }) => {
        useEffect(() => {
          // Update values as soon as they are edited.
          if (dirty) {
            eventCreation.update(values);
          }
        }, [values]);

        const publishEvent = async () => {
          await eventCreation.publish(values);

          router.push(`/events/${eventCreation.eventId}`);
          showToast({
            icon: '🙌',
            message: 'Big moves! Your event is live.'
          });
        };

        const isOffersScreen = screen === Screens.OFFERS;
        // Not the most elegant solution, but limited by the implementation of create-event page
        const title = isOffersScreen
          ? getOffersStepTitle(offersStep)
          : currentScreen.title;

        return (
          <Form>
            <Header
              isDraft={values.isDraft}
              isEditing={!!values._id}
              title={title}
              isLoading={eventCreation.isLoading}
              lastUpdated={eventCreation.lastUpdated}
              onBack={() => {
                if (!currentScreen?.prevScreen) {
                  router.back();

                  if (isBounceWebview()) {
                    postMessageToRN(WEBVIEW_CLOSE);
                  }
                } else if (isOffersScreen) {
                  // Not the most elegant solution, but limited by the implementation of create-event page
                  handleOfferBackNavigation();
                } else {
                  setScreen(currentScreen.prevScreen);
                }
              }}
              onMenuClick={onClickEventSettings}
            />

            {currentScreen.renderComponent({
              eventId: eventCreation.eventId,
              isLoading: eventCreation.isLoading,
              shouldRequestToPublish: eventCreation.shouldRequestToPublish,
              requestToPublishFormId: eventCreation.requestToPublishFormId,
              createDraft: eventCreation.createDraft,
              onClickEventSettings,
              navigateToRecurringSettings,
              togglePricingTiersModal,
              navigateToTrackingLinks,
              navigateToDiscountCodes,
              navigateToSeriesSettings,
              navigateToOffers,
              navigateToBasicForm,
              requestPublication,
              refetchEvent: eventCreation.refetchEvent,
              eventSeriesId: eventCreation.initialValues.seriesId,
              offersStep,
              setOffersStep,
              handleOfferBackNavigation
            })}

            {showPricingTiers && (
              <PricingTiersModal
                onClose={togglePricingTiersModal}
                eventId={eventCreation.eventId}
                eventShareToken={eventCreation.initialValues.shareToken}
                refetchEvent={eventCreation.refetchEvent}
                createDraft={eventCreation.createDraft}
              />
            )}

            {showPublishModal && (
              <PublishModal
                onClose={togglePublishModal}
                onPublish={publishEvent}
                isLoading={eventCreation.isLoading}
              />
            )}

            {showPublicationRequestedModal && (
              <PublicationRequestedModal eventCoverImgUrl={values.photo.url} />
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default CreateEvent;
